import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { Button } from '@invento/ui/components/button';
import { RouteState } from '@invento/ui/components/route-state';
import { ROUTE_PATHS } from './constants';

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // The application-level error reporter can observe this boundary without
    // exposing implementation details in the customer-facing state.
  }

  private retry = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="min-h-dvh bg-[var(--a-page)] p-4 sm:p-6">
        <RouteState
          variant="error"
          title="We couldn’t load this page"
          description="Something interrupted the page before it could finish loading. Try again or return to the dashboard."
          primaryAction={
            <Button type="button" size="dense" variant="primary" onClick={this.retry}>
              Try again
            </Button>
          }
          secondaryAction={
            <Button
              type="button"
              size="dense"
              variant="secondary"
              onClick={() => {
                if (typeof window !== 'undefined') window.history.back();
              }}
            >
              Go back
            </Button>
          }
          footer={`Open ${ROUTE_PATHS.HOME} if the problem continues.`}
        />
      </main>
    );
  }
}
